import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CopyIcon, KeyIcon } from "@phosphor-icons/react";
import { fetchGrokTokens, createGrokToken, revokeGrokToken } from "../../lib/api";
import { Input, Select } from "../ui-bits";

export function TokensPanel() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("wa:read");
  const [fresh, setFresh] = useState(null);

  const load = useCallback(async () => {
    try { setTokens(await fetchGrokTokens()); } catch { /* empty */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return toast.error("Give the token a name");
    try {
      const t = await createGrokToken({ name: name.trim(), scopes: scope === "wa:write" ? ["wa:read", "wa:write"] : ["wa:read"] });
      setFresh(t.token); setName(""); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create token"); }
  };
  const revoke = async (id) => {
    try { await revokeGrokToken(id); setFresh(null); toast.success("Token revoked"); await load(); }
    catch { toast.error("Could not revoke"); }
  };
  const copy = () => { navigator.clipboard?.writeText(fresh); toast.success("Copied"); };

  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid="grok-tokens-panel">
      <div className="overline mb-1 flex items-center gap-1.5"><KeyIcon size={13} weight="bold" /> Bearer tokens</div>
      <div className="font-heading font-black text-lg mb-3">API access for Grok</div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input data-testid="grok-token-name-input" placeholder="Token name, e.g. grok-prod" value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
        <Select data-testid="grok-token-scope-select" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="wa:read">Read only (wa:read)</option>
          <option value="wa:write">Read + write (wa:write)</option>
        </Select>
        <button data-testid="grok-token-create-btn" onClick={create} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-zinc-800">Create</button>
      </div>
      {fresh && (
        <div className="border-2 border-zinc-950 p-3 mb-3" data-testid="grok-token-fresh">
          <div className="text-xs font-semibold mb-1.5">Copy it now — it is shown only once.</div>
          <div className="flex gap-2 items-center">
            <code data-testid="grok-token-value" className="flex-1 text-xs font-mono-num bg-zinc-100 px-2 py-1.5 break-all">{fresh}</code>
            <button data-testid="grok-token-copy-btn" onClick={copy} className="p-2 border border-zinc-300 hover:bg-zinc-50" aria-label="Copy token"><CopyIcon size={14} /></button>
          </div>
        </div>
      )}
      <div className="divide-y divide-zinc-100">
        {tokens.map((t) => (
          <div key={t.id} data-testid={`grok-token-row-${t.id}`} className="flex items-center gap-3 py-2.5 text-sm">
            <span className="font-medium flex-1 min-w-0 truncate">{t.name} <span className="text-zinc-400 font-mono-num text-xs">{t.prefix}…</span></span>
            <span className="text-xs font-mono-num text-zinc-500">{t.scopes.join(" ")}</span>
            {t.revoked ? <span className="text-xs text-zinc-400">revoked</span> : (
              <button data-testid={`grok-token-revoke-${t.id}`} onClick={() => revoke(t.id)} className="text-xs font-semibold text-[#FF3B30] hover:underline">Revoke</button>
            )}
          </div>
        ))}
        {tokens.length === 0 && <div className="text-sm text-zinc-400 py-4 text-center">No tokens yet.</div>}
      </div>
    </div>
  );
}
